/** Thin typed client for /api/journey/*. The server decides access and what to show; nothing here is trusted. */
import type { JourneySurface, SituationKind, WireNudge, Situation } from '../../../types/journey';

export interface JourneyNextOptions {
  jobId?: string;
  platform?: 'web' | 'app';
  /** Search results header: the search is remote / is a summer job search. */
  remote?: boolean;
  summer?: boolean;
  /** Job page fallbacks when the server cannot read the listing row. */
  daysOpen?: number;
  finnishRequired?: boolean;
  /** Language of the copy. Default: the page language. */
  locale?: 'en' | 'fi' | 'sv';
}

function pageLocale(): 'en' | 'fi' | 'sv' | undefined {
  if (typeof document === 'undefined') return undefined;
  const lang = document.documentElement.lang?.slice(0, 2);
  return lang === 'fi' || lang === 'sv' || lang === 'en' ? lang : undefined;
}

export function nextQueryString(surface: JourneySurface, options: JourneyNextOptions = {}): string {
  const params = new URLSearchParams({ surface });
  const locale = options.locale ?? pageLocale();
  if (locale) params.set('locale', locale);
  if (options.jobId) params.set('jobId', options.jobId);
  if (options.platform) params.set('platform', options.platform);
  if (options.remote !== undefined) params.set('remote', options.remote ? '1' : '0');
  if (options.summer !== undefined) params.set('summer', options.summer ? '1' : '0');
  if (options.daysOpen !== undefined) params.set('daysOpen', String(Math.max(0, Math.round(options.daysOpen))));
  if (options.finnishRequired !== undefined) params.set('finnishRequired', options.finnishRequired ? '1' : '0');
  return params.toString();
}

/** One nudge for a surface, or null. Any failure reads as "nothing to show": a nudge never breaks a page. */
export async function fetchNext(surface: JourneySurface, options: JourneyNextOptions = {}): Promise<WireNudge | null> {
  try {
    const response = await fetch(`/api/journey/next?${nextQueryString(surface, options)}`, { credentials: 'same-origin' });
    if (!response.ok) return null;
    const body = (await response.json()) as { nudge?: WireNudge | null };
    return body.nudge ?? null;
  } catch {
    return null;
  }
}

async function report(kind: 'acted' | 'dismissed', nudge: Pick<WireNudge, 'ruleId' | 'subject' | 'surface'>): Promise<void> {
  try {
    await fetch(`/api/journey/${kind}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ruleId: nudge.ruleId, surface: nudge.surface, subject: nudge.subject }),
      keepalive: true,
    });
  } catch {
    // Best effort: the card has already done its job.
  }
}

export const reportActed = (nudge: Pick<WireNudge, 'ruleId' | 'subject' | 'surface'>) => report('acted', nudge);
export const reportDismissed = (nudge: Pick<WireNudge, 'ruleId' | 'subject' | 'surface'>) => report('dismissed', nudge);

export async function saveSituation(input: { kind: SituationKind; noticeEnd?: string }): Promise<{ situation: Situation; journey?: { nudge: WireNudge; surface: JourneySurface } } | null> {
  try {
    const response = await fetch('/api/journey/situation', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!response.ok) return null;
    return (await response.json()) as { situation: Situation; journey?: { nudge: WireNudge; surface: JourneySurface } };
  } catch {
    return null;
  }
}

export async function fetchSituation(): Promise<Situation | null> {
  try {
    const response = await fetch('/api/journey/situation', { credentials: 'same-origin' });
    if (!response.ok) return null;
    return ((await response.json()) as { situation: Situation | null }).situation;
  } catch {
    return null;
  }
}

/** A save response may carry a nudge picked at event time. */
export function nudgeFromSaveResponse(body: unknown): { nudge: WireNudge; surface: JourneySurface } | null {
  const journey = (body as { journey?: { nudge?: WireNudge; surface?: JourneySurface } } | null)?.journey;
  return journey?.nudge && journey.surface ? { nudge: journey.nudge, surface: journey.surface } : null;
}
